import React, { useContext, useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { navigate } from 'gatsby'
import { useInView } from 'react-intersection-observer'
import { LayoutContext, useUI } from '../../contexts'
import { getSessionStorageCurrencyUpdatedKey } from '../../utils'
import CheckoutCurrencyReplace from '../shared/CheckoutCurrencyReplace'
import {
  matchCountryToLanguage,
  matchCurrency,
} from '../CurrencyConverterSwitcher'
import { desktopVW, mobileVW } from '../../styles'
import {
  useNotificationClosedTracking,
  useCurrencySelectedTracking,
  useNotificationViewedTracking,
} from '../../hooks/tracking'
import { useShopify } from '../../contexts/shopify'
import { useGlobal } from '../../hooks/useGlobal'

const CurrencyPopIn = () => {
  const { setCurrencyPopIn, currencyPopIn: popIn } = useShopify()

  const { setNodeLocale } = useUI()

  const {
    node_locale: nodeLocale,
    currency: currentCurrency,
    storeSubdomain,
    alternateLang,
  } = useContext(LayoutContext)

  const {
    layout: {
      dutch,
      german,
      english,
      french,
      euros,
      britishPounds,
      currencyPopInTextV2,
      americanDollars,
    },
  } = useGlobal(['layout'], nodeLocale)

  const replaceCheckoutCurrency = CheckoutCurrencyReplace()

  const multiLang = {
    dutch,
    german,
    english,
    french,
  }

  const currencies = {
    USD: americanDollars,
    GBP: britishPounds,
    EUR: euros,
  }

  const [newCurrency, setNewCurrency] = useState()
  const [language, setLanguage] = useState()
  const [node_locale, set_node_locale] = useState()

  /**
   * Currency Popin Richtext
   */
  const currencyPopInRichText = JSON.parse(currencyPopInTextV2?.raw ?? '{}')

  // IE doesn't support intersection observer, breaks the site
  const isIE =
    /*@cc_on!@*/ false ||
    (typeof window !== `undefined` && !!document.documentMode)
  const [ref, inView] = isIE
    ? []
    : useInView({ triggerOnce: true, trackVisibility: true, delay: 100 })

  useEffect(() => {
    if (popIn) {
      setNewCurrency(popIn.currency)
      matchCountryToLanguage(
        popIn.country,
        setLanguage,
        set_node_locale,
        multiLang,
      )
    }
  }, [popIn])

  useEffect(() => {
    if (!isIE) {
      if (inView) {
        useNotificationViewedTracking({
          currency: currentCurrency.code,
          locale: node_locale,
          notificationHeadline: `${
            currencyPopInRichText.content[0]?.content[0].value
          } ${matchCurrency(newCurrency?.code, currencies)}${
            currencyPopInRichText.content[1]?.content[0].value
          }`,
          notificationType: 'Currency',
        })
      }
    }
  }, [!isIE && inView])

  const redirectHandler = () => {
    const slug = alternateLang.find(
      ({ node_locale: locale }) => node_locale === locale,
    ).slug
    setNodeLocale(node_locale)
    setCurrencyPopIn(null)
    navigate(`/${node_locale}/${slug ? slug + '/' : ''}`)
    replaceCheckoutCurrency(newCurrency.code)
    setSessionStorage()
    useCurrencySelectedTracking({
      currency: currentCurrency.code,
      locale: node_locale,
      placement: 'Notification',
      label: matchCurrency(newCurrency?.code, currencies),
    })
  }

  const closeButtonHandler = () => {
    setCurrencyPopIn(null)
    setSessionStorage()
    useNotificationClosedTracking({
      currency: currentCurrency.code,
      locale: node_locale,
      notificationHeadline: `${
        currencyPopInRichText.content[0]?.content[0].value
      } ${matchCurrency(newCurrency?.code, currencies)}${
        currencyPopInRichText.content[1]?.content[0].value
      }`,
      notificationType: 'Currency',
    })
  }

  const setSessionStorage = () => {
    sessionStorage.setItem(
      getSessionStorageCurrencyUpdatedKey(storeSubdomain),
      JSON.stringify(true),
    )
  }

  if (!popIn) return null

  return (
    <StyledCurrencyPopIn visible={popIn} ref={ref}>
      <Text>
        {currencyPopInRichText.content[0]?.content[0].value}{' '}
        {matchCurrency(newCurrency?.code, currencies)}
        {currencyPopInRichText.content[1]?.content[0].value}
      </Text>
      <ButtonWrapper>
        <StyledButton onClick={() => closeButtonHandler()}>
          {matchCurrency(currentCurrency?.code, currencies)}
        </StyledButton>
        <StyledButton primary onClick={() => redirectHandler()}>
          {matchCurrency(newCurrency?.code, currencies)}
        </StyledButton>
      </ButtonWrapper>
      <Close onClick={() => closeButtonHandler()}>
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'>
          <path d='M14.53 4.53l-1.06-1.06L9 7.94 4.53 3.47 3.47 4.53 7.94 9l-4.47 4.47 1.06 1.06L9 10.06l4.47 4.47 1.06-1.06L10.06 9z' />
        </svg>
      </Close>
    </StyledCurrencyPopIn>
  )
}

const fadeIn = keyframes`
  from {
    visibility: 'hidden';
    opacity: 0;
  }
  to {
    visibility: 'visible';
    opacity: 1;
  }
`

const slideUp = keyframes`
from {
  transform: translateY(100%);
}
to {
  transform: translateY(0);
}
`

const StyledCurrencyPopIn = styled.div`
  box-sizing: border-box;
  position: fixed;
  z-index: 999999;
  box-shadow: 0px 0px 15px 1px rgba(0, 0, 0, 0.15);
  background: ${(props) => props.theme.colors.blue};
  bottom: 0;
  left: 0;
  right: 0;
  padding: 4vw 11vw 5vw;
  transform: translateY(100%);
  animation: ${slideUp} 1s 5s ${(props) => props.theme.easeOut.expo} forwards;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    bottom: 2.5vw;
    left: auto;
    right: 2.5vw;
    width: 24vw;
    padding: ${(props) => props.theme.space.marginDesktop * 2.1}vw
      ${(props) => props.theme.space.marginDesktop * 2.4}vw;
    transform: translateY(0);
    opacity: 0;
    animation: ${fadeIn} 1s 5s ${(props) => props.theme.easeOut.expo} forwards;
  }
`

const Text = styled.p`
  color: ${(props) => props.theme.colors.white};
  margin: 0 auto;
  line-height: 1.3;
  text-align: left;
  width: 100%;
  font-size: 4.3vw;
  margin-bottom: 2.8vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    text-align: left;
    line-height: 1.5;
    font-size: 1.2vw;
    margin-bottom: ${(props) => props.theme.space.marginDesktop * 1.3}vw;
  }
`

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    justify-content: space-between;
  }
`

const StyledButton = styled.button`
  color: ${(props) =>
    props.primary ? props.theme.colors.blue : props.theme.colors.white};
  background: ${(props) =>
    props.primary ? props.theme.colors.white : props.theme.colors.blue};
  border: ${(props) =>
    props.primary ? 'none' : `1px solid ${props.theme.colors.white}`};
  cursor: pointer;
  font-size: ${(props) => props.theme.fontSize.m30};
  padding: ${(props) => props.theme.space.marginMobile * 0.7}vw
    ${(props) => props.theme.space.marginMobile * 0.8}vw;
  width: ${mobileVW(135)};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 1.2vw;
    padding: ${(props) => props.theme.space.marginDesktop * 0.9}vw
      ${(props) => props.theme.space.marginDesktop * 1.1}vw;
    width: ${desktopVW(185)};
    margin: 0;
  }
`

const Close = styled.button`
  position: absolute;
  top: 1.5vw;
  right: 1.5vw;
  cursor: pointer;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    top: ${(props) => props.theme.space.marginDesktop * 0.9}vw;
    right: ${(props) => props.theme.space.marginDesktop * 0.9}vw;
  }

  svg {
    fill: ${(props) => props.theme.colors.white};
    opacity: 0.6;
    width: 5vw;
    height: 5vw;
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      width: 1.3vw;
      height: 1.3vw;
    }
  }
`

export default CurrencyPopIn
